import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { IncomingInventorizationOrder, InitialInventorizationPallet } from '../services/configApi';
import { getIncomingOrderLabel, getMatchedPalletOrder } from '../utils/inventorizationOrder';
import {
  formatQuantity,
  isQuantityProvided,
  isQuantityValid,
  parseQuantity,
} from '../utils/inventorizationQuantity';

const SEARCH_DEBOUNCE_MS = 1000;

// Leaving the dialog throws the scan away, so only the Cancel button may close it — a stray
// tap beside a tablet dialog must not cost the operator the pallet they just scanned.
const NO_ACCIDENTAL_DISMISS_PROPS = { disableBackdropClick: true, disableEscapeKeyDown: true };

interface InitialPalletSearchDialogProps {
  /** The code that was scanned and matched no pallet by SSCC, or null while the dialog fades out. */
  sscc: string | null;
  disabled?: boolean;
  /** Shown here because the panel behind the dialog is out of sight. */
  error?: string | null;
  findPalletByExtId: (extId: string) => InitialInventorizationPallet | undefined;
  /** A number carried by several pallets cannot be matched; the bare number can still be stored. */
  isExtIdAmbiguous: (extId: string) => boolean;
  /** The incoming orders the pallet may have arrived with, offered once nothing matched. */
  incomingOrders: IncomingInventorizationOrder[];
  findIncomingOrderById: (orderId: number) => IncomingInventorizationOrder | undefined;
  onMatched: (pallet: InitialInventorizationPallet, quantity: number | null) => void;
  onStore: (palletExtId: string, quantity: number | null, orderId: number | null) => void;
  onCancel: () => void;
}

/**
 * Where a scanned pallet reaches when its SSCC is unknown: the operator reads the pallet
 * number off the label instead, and the record is written either against the pallet that
 * number belongs to or against the bare number. The boxes are proposed from the pallet the
 * number was found on and counted by hand for everything else. A number that belongs to no
 * pallet leaves the incoming order as the only thing saying where the pallet came from, so
 * that is the point at which the operator is asked to pick one.
 */
export const InitialPalletSearchDialog: React.FC<InitialPalletSearchDialogProps> = ({
  sscc,
  disabled,
  error,
  findPalletByExtId,
  isExtIdAmbiguous,
  incomingOrders,
  findIncomingOrderById,
  onMatched,
  onStore,
  onCancel,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [palletNumber, setPalletNumber] = useState('');
  const [searchedNumber, setSearchedNumber] = useState('');
  const [quantity, setQuantity] = useState('');
  const [replacedQuantity, setReplacedQuantity] = useState<string | null>(null);
  const [pickedOrder, setPickedOrder] = useState<IncomingInventorizationOrder | null>(null);
  const typedQuantityRef = useRef('');

  const trimmedNumber = palletNumber.trim();

  useEffect(() => {
    const timer = setTimeout(() => setSearchedNumber(trimmedNumber), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedNumber]);

  // A fresh SSCC is a fresh pallet: nothing typed for the previous one may carry over.
  useEffect(() => {
    setPalletNumber('');
    setSearchedNumber('');
    setQuantity('');
    setReplacedQuantity(null);
    setPickedOrder(null);
    typedQuantityRef.current = '';
  }, [sscc]);

  const searchedExtId = /^\d+$/.test(searchedNumber) ? searchedNumber : null;
  const matchedPallet = searchedExtId === null ? undefined : findPalletByExtId(searchedExtId);
  const isAmbiguous = searchedExtId !== null && isExtIdAmbiguous(searchedExtId);
  const isSearching = trimmedNumber !== searchedNumber;
  const isQuantityMalformed = !isQuantityValid(quantity);
  const canWriteRecord = isQuantityProvided(quantity);

  const matchedOrder = matchedPallet
    ? getMatchedPalletOrder(matchedPallet, findIncomingOrderById)
    : null;
  const isOrderLookupEnabled = !isSearching && searchedExtId !== null && !matchedPallet;

  // What the found pallet carries is a proposal — the operator overwrites it with what is counted.
  // A count already typed is replaced by it, so the number that was lost is named back to them.
  useEffect(() => {
    const proposed = formatQuantity(matchedPallet?.quantity);
    const typed = typedQuantityRef.current;

    setReplacedQuantity(typed !== '' && typed !== proposed ? typed : null);
    setQuantity(proposed);
    typedQuantityRef.current = '';
  }, [matchedPallet]);

  const handleQuantityChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value.trim();
    typedQuantityRef.current = value;
    setReplacedQuantity(null);
    setQuantity(value);
  };

  const getHelperText = (): string | undefined => {
    if (isSearching || searchedExtId === null) return undefined;
    if (isAmbiguous) {
      return translate('osfc.storage_ui.inventorization.pallet_search.ambiguous_number');
    }
    if (!matchedPallet) {
      return translate('osfc.storage_ui.inventorization.pallet_search.not_found');
    }
    return undefined;
  };

  return (
    <Dialog open={!!sscc} {...NO_ACCIDENTAL_DISMISS_PROPS} maxWidth="xs" fullWidth>
      <DialogTitle>
        {translate('osfc.storage_ui.inventorization.pallet_search.title')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Typography variant="body2" style={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
            {error}
          </Typography>
        )}

        <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
          {translate('osfc.storage_ui.inventorization.pallet_search.scanned_sscc', { value: sscc })}
        </Typography>

        <TextField
          label={translate('osfc.storage_ui.inventorization.pallet_search.number_label')}
          variant="outlined"
          size="small"
          fullWidth
          autoFocus
          value={palletNumber}
          onChange={(e) => setPalletNumber(e.target.value)}
          style={{ marginTop: '8px' }}
          helperText={getHelperText()}
        />

        <TextField
          label={translate('osfc.inventorization.columns.quantity')}
          variant="outlined"
          size="small"
          fullWidth
          required
          value={quantity}
          onChange={handleQuantityChange}
          style={{ marginTop: '8px' }}
          error={isQuantityMalformed}
          helperText={isQuantityMalformed
            ? translate('osfc.storage_ui.inventorization.invalid_quantity')
            : undefined}
        />

        {replacedQuantity !== null && (
          <Typography
            variant="caption"
            style={{ display: 'block', marginTop: '4px', color: theme.palette.warning.main }}
          >
            {translate('osfc.storage_ui.inventorization.pallet_search.quantity_replaced', {
              value: replacedQuantity,
            })}
          </Typography>
        )}

        {matchedPallet ? (
          <TextField
            label={translate('osfc.storage_ui.inventorization.pallet_search.order_label')}
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginTop: '8px' }}
            value={matchedOrder ? getIncomingOrderLabel(matchedOrder) : '-'}
            disabled
          />
        ) : (
          <Autocomplete<IncomingInventorizationOrder, false, false, false>
            options={incomingOrders}
            getOptionLabel={getIncomingOrderLabel}
            getOptionSelected={(option, value) => option.order_id === value.order_id}
            value={pickedOrder}
            onChange={(_event, order) => setPickedOrder(order)}
            disabled={disabled || !isOrderLookupEnabled}
            size="small"
            style={{ marginTop: '8px' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('osfc.storage_ui.inventorization.pallet_search.order_label')}
                variant="outlined"
                helperText={isOrderLookupEnabled
                  ? undefined
                  : translate('osfc.storage_ui.inventorization.pallet_search.order_hint')}
              />
            )}
          />
        )}

        <Button
          variant="contained"
          fullWidth
          style={{
            marginTop: '8px',
            backgroundColor: matchedPallet && !disabled ? theme.palette.success.main : undefined,
            color: matchedPallet && !disabled ? theme.palette.common.white : undefined,
          }}
          disabled={disabled || isSearching || !matchedPallet || !canWriteRecord}
          onClick={() => matchedPallet && onMatched(matchedPallet, parseQuantity(quantity))}
        >
          {translate('osfc.storage_ui.inventorization.pallet_search.matched')}
        </Button>

        <Button
          variant="contained"
          fullWidth
          style={{ marginTop: '8px' }}
          disabled={disabled || isSearching || searchedExtId === null || !!matchedPallet || !canWriteRecord}
          onClick={() => searchedExtId !== null
            && onStore(searchedExtId, parseQuantity(quantity), pickedOrder?.order_id ?? null)}
        >
          {translate('osfc.storage_ui.inventorization.pallet_search.store')}
        </Button>

        <Typography
          variant="caption"
          style={{ display: 'block', marginTop: '8px', color: theme.palette.warning.main }}
        >
          {translate('osfc.storage_ui.inventorization.pallet_search.cancel_warning')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={disabled}>
          {translate('osfc.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
